import { useMutation } from "@apollo/client";
import { Edit, Delete, MoreVert } from "@mui/icons-material";
import {
  Card,
  CardActions,
  CardContent,
  CardMedia,
  Chip,
  Grid,
  IconButton,
  Typography,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Box,
  type ChipProps,
} from "@mui/material";
import React, { memo, useContext, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  DELETE_PROJECT,
  PROJECTS,
  GET_PROJECTS_BY_STATUS, // ← replace PUBLIC_PROJECTS
} from "../graphql/gqlProject";
import { UserContext } from "../contexts/userContext";
import { SnackbarContext } from "../contexts/snackbarContext";
import { T } from "./T";
import { ProjectMeta, Status } from "../graphql/gqlTypes";
import { AreYouSureModal } from "../modals/AreYouSureModal";
import EditProjectModal from "../modals/EditProjectModal";

const statusLabel = (s: Status) =>
  s === "PRIVATE" ? (
    <T str="Private" />
  ) : s === "SHARED" ? (
    <T str="Shared" />
  ) : (
    <T str="Public" />
  );

const statusChipColor = (s: Status): ChipProps["color"] =>
  s === "PUBLIC" ? "success" : s === "SHARED" ? "warning" : "primary";

const ProjectCard_ = ({ project }: { project: ProjectMeta }) => {
  const { id, name, status, description, owner } = project;
  const navigate = useNavigate();
  const { userId } = useParams();
  const { user } = useContext(UserContext);
  const { openSuccessSnackbar } = useContext(SnackbarContext);

  const [deleteProject] = useMutation(DELETE_PROJECT, {
    onCompleted: () => {
      openSuccessSnackbar(<T str="Project successfully deleted" />);
    },
    refetchQueries: [
      { query: PROJECTS, variables: { userId } },
      {
        query: GET_PROJECTS_BY_STATUS,
        variables: {
          status: "PUBLIC",
          pagination: { cursor: null, limit: 12 },
        },
      },
    ],
    onError: (error) => console.error(error),
  });

  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [isDeleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isEditDialogOpen, setEditDialogOpen] = useState(false);

  const isMenuOpen = Boolean(menuAnchor);

  const openMenu = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setMenuAnchor(event.currentTarget);
  };

  const closeMenu = () => setMenuAnchor(null);

  const navigateToProject = () => {
    if (!isMenuOpen) {
      navigate(`/${owner?.id || "unknownUser"}/projects/${id}`);
    }
  };

  const handleDeleteConfirm = () => {
    deleteProject({ variables: { id } });
    setDeleteDialogOpen(false);
  };

  return (
    <Grid item xs={12} sm={6} md={4} lg={3}>
      <Card
        onClick={navigateToProject}
        sx={{
          position: "relative",
          cursor: "pointer",
          transition: "box-shadow 0.3s",
          "&:hover": { boxShadow: 4 },
          height: 400,
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          margin: "auto",
          pb: 2,
        }}
      >
        <CardMedia
          component="img"
          height="200"
          image={
            "https://www.scoutsengidsenvlaanderen.be/sites/default/files/styles/og_image/public/images/2021-Mieke%20De%20Nys%20%286%29.jpg?itok=t3z8clh7"
          }
          alt={name}
          sx={{ objectFit: "cover" }}
        />

        {/* Status Chip */}
        <Box sx={{ position: "absolute", top: 8, left: 8 }}>
          <Chip
            label={statusLabel(status)}
            color={statusChipColor(status)}
            size="small"
            variant="filled"
          />
        </Box>

        <CardContent sx={{ flexGrow: 1 }}>
          <Tooltip title={name.length > 25 ? name : ""} arrow>
            <Typography
              variant="h5"
              noWrap
              sx={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {name}
            </Typography>
          </Tooltip>

          <Typography
            variant="body2"
            sx={{
              display: "-webkit-box",
              WebkitLineClamp: 3,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              textOverflow: "ellipsis",
              fontSize: 12,
              py: 1,
            }}
          >
            {owner?.firstName && owner?.lastName
              ? `${owner.firstName} ${owner.lastName}`
              : "Unknown user"}
          </Typography>

          <Typography
            variant="body2"
            sx={{
              display: "-webkit-box",
              WebkitLineClamp: 3,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {description}
          </Typography>
        </CardContent>

        {owner?.id && user?.id && owner.id === user.id && (
          <CardActions sx={{ justifyContent: "flex-end" }}>
            <IconButton onClick={openMenu}>
              <MoreVert />
            </IconButton>
            <Menu anchorEl={menuAnchor} open={isMenuOpen} onClose={closeMenu}>
              <MenuItem
                onClick={() => {
                  setEditDialogOpen(true);
                  closeMenu();
                }}
              >
                <ListItemIcon>
                  <Edit fontSize="small" />
                </ListItemIcon>
                <ListItemText>
                  <T str="Edit" />
                </ListItemText>
              </MenuItem>
              <MenuItem
                onClick={() => {
                  setDeleteDialogOpen(true);
                  closeMenu();
                }}
              >
                <ListItemIcon>
                  <Delete fontSize="small" />
                </ListItemIcon>
                <ListItemText>
                  <T str="Delete" />
                </ListItemText>
              </MenuItem>
            </Menu>
          </CardActions>
        )}
      </Card>

      <AreYouSureModal
        title={<T str="Are you sure?" />}
        question={<T str="This action cannot be undone!" />}
        confirm={<T str="Delete" />}
        onConfirm={handleDeleteConfirm}
        open={isDeleteDialogOpen}
        setOpen={setDeleteDialogOpen}
      />

      <EditProjectModal
        isOpen={isEditDialogOpen}
        close={() => setEditDialogOpen(false)}
        project={project}
      />
    </Grid>
  );
};

const ProjectCard = memo(ProjectCard_);
export default ProjectCard;
